import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Bars3Icon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { BookContext } from "../../contexts/BookContext";
import { logo } from "../../images";
import { selectUser } from "../../store/slices/auth";
import { useSelector } from "../../store/store";
import { theme } from "../../utils/theme";
import { GenerateBookAIButton } from "../Buttons/GenerateBookAIButton";
import { GenerateBookPDFButton } from "../Buttons/GenerateBookPDFButton";
import { LanguageSelectorModalButton } from "../Buttons/LanguageSelectorModalButton";
import { UpdateProfileModal } from "../Modals/UpdateProfileModal";

export const Nav = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = () => {
    navigate("/login", { state: { logout: true } });
  };
  const { book } = useContext(BookContext);
  const user = useSelector(selectUser);
  const genAIStatus = ["draft", "ready_to_generate"];
  const genPDFStatus = ["ai_generated", "completed"];

  return (
    <>
      <Disclosure as="nav" className="bg-white shadow">
        <div className="mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex h-14 sm:h-16 justify-between">
            <div className="flex">
              <div className="-ml-2 mr-2 flex items-center md:hidden">
                {/* Mobile menu button */}
                <DisclosureButton className="group relative inline-flex items-center justify-center rounded p-1.5 sm:p-2 text-slate-400 hover:bg-gray-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-delicate-500">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon
                    aria-hidden="true"
                    className="block size-5 sm:size-6 group-data-[open]:hidden"
                  />
                  <XMarkIcon
                    aria-hidden="true"
                    className="hidden size-5 sm:size-6 group-data-[open]:block"
                  />
                </DisclosureButton>
              </div>
              <div className="flex shrink-0 items-center">
                <a href="/">
                  <img
                    alt="Memoirs Logo"
                    src={logo.logoGrey}
                    className="h-16 sm:h-18 lg:h-20 w-auto"
                  />
                </a>
              </div>
              <div className="hidden md:ml-6 md:flex md:space-x-8"></div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {book && (
                <div className="shrink-0">
                  {genAIStatus.includes(book.status) && (
                    <GenerateBookAIButton />
                  )}
                  {genPDFStatus.includes(book.status) && (
                    <GenerateBookPDFButton />
                  )}
                </div>
              )}
              <div className="hidden md:ml-4 md:flex md:shrink-0 md:items-center md:space-x-2">
                {/* Language Selector */}
                <LanguageSelectorModalButton />

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <MenuButton className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      <UserIcon
                        className="size-5 sm:size-6 m-1"
                        aria-hidden="true"
                        style={{ color: theme.colors.slate[50] }}
                      />
                    </MenuButton>
                  </div>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <MenuItem>
                      <a
                        onClick={() => setOpen(true)}
                        className="block px-4 py-2 text-sm text-slate-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                      >
                        {t("nav.profile")}
                      </a>
                    </MenuItem>
                    {user?.is_admin && (
                      <MenuItem>
                        <a
                          onClick={() => navigate("/admin")}
                          className="block px-4 py-2 text-sm font-medium text-indigo-600 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                        >
                          Administration
                        </a>
                      </MenuItem>
                    )}
                    <MenuItem>
                      <a
                        href={import.meta.env.VITE_MEMOIRS_NAV_GUIDE_URL}
                        target="_blank"
                        className="block px-4 py-2 text-sm text-slate-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                      >
                        {t("nav.usageGuide")}
                      </a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        href="/guide-pour-écrire-vos-mémoires-avec-sérénité.pdf"
                        target="_blank"
                        className="block px-4 py-2 text-sm text-slate-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                      >
                        {t("nav.writingGuide")}
                      </a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        onClick={() => logout()}
                        className="block px-4 py-2 text-sm text-slate-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                      >
                        {t("logout.logout")}
                      </a>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>
        </div>

        <DisclosurePanel className="md:hidden">
          <div className="space-y-1 pb-3 pt-2">
            {/* Current: "bg-delicate-50 border-delicate-500 text-delicate-700", Default: "border-transparent text-slate-500 hover:bg-gray-50 hover:border-gray-300 hover:text-slate-700" */}
            {/* <DisclosureButton
              as="a"
              href="#"
              className="block border-l-4 border-delicate-500 bg-delicate-50 py-2 pl-3 pr-4 text-base font-medium text-delicate-700 sm:pl-5 sm:pr-6"
            >
              Dashboard
            </DisclosureButton> */}
          </div>
          <div className="border-t border-gray-200 pb-3 pt-4">
            <div className="flex items-center px-3 sm:px-4 lg:px-6">
              <div className="shrink-0">
                <UserIcon
                  className="size-5 sm:size-6 m-1"
                  aria-hidden="true"
                  style={{ color: theme.colors.slate[50] }}
                />
              </div>
            </div>
            <div className="mt-3 space-y-1 px-2 sm:px-4">
              <div className="px-2 py-2">
                <LanguageSelectorModalButton />
              </div>

              <DisclosureButton
                as="div"
                className="block px-2 py-2 text-sm sm:text-base font-medium text-slate-500 hover:bg-gray-100 hover:text-slate-800 rounded"
              >
                <a onClick={() => setOpen(true)}>{t("nav.profile")}</a>
              </DisclosureButton>
              {user?.is_admin && (
                <DisclosureButton
                  as="div"
                  className="block px-2 py-2 text-sm sm:text-base font-medium text-indigo-600 hover:bg-gray-100 rounded"
                >
                  <a onClick={() => navigate("/admin")}>Administration</a>
                </DisclosureButton>
              )}
              <DisclosureButton
                as="div"
                className="block px-2 py-2 text-sm sm:text-base font-medium text-slate-500 hover:bg-gray-100 hover:text-slate-800 rounded"
              >
                <a
                  href={import.meta.env.VITE_MEMOIRS_NAV_GUIDE_URL}
                  target="_blank"
                >
                  {t("nav.usageGuide")}
                </a>
              </DisclosureButton>
              <DisclosureButton
                as="div"
                className="block px-2 py-2 text-sm sm:text-base font-medium text-slate-500 hover:bg-gray-100 hover:text-slate-800 rounded"
              >
                <a
                  href="/guide-pour-écrire-vos-mémoires-avec-sérénité.pdf"
                  target="_blank"
                >
                  {t("nav.writingGuide")}
                </a>
              </DisclosureButton>
              <DisclosureButton
                as="a"
                onClick={() => logout()}
                className="block px-2 py-2 text-sm sm:text-base font-medium text-slate-500 hover:bg-gray-100 hover:text-slate-800 rounded"
              >
                {t("logout.logout")}
              </DisclosureButton>
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
      <UpdateProfileModal open={open} setOpen={setOpen} />
    </>
  );
};
